import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'react-hot-toast';
import { 
  Mail, Send, Trash2, AlertTriangle, Eye, 
  MapPin, Phone, MessageSquare, Calendar, User, 
  CheckCircle, Clock, AlertCircle, XCircle, FileText 
} from 'lucide-react';
import { AuthContext } from '../App';

const GestionConsultasVisitantes = () => {
  const { API } = useContext(AuthContext);
  const [consultas, setConsultas] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [consultaSeleccionada, setConsultaSeleccionada] = useState(null);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  
  // Estados para respuesta con plantillas
  const [mostrarDialogoRespuesta, setMostrarDialogoRespuesta] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('general');
  const [mensajePersonalizado, setMensajePersonalizado] = useState('');
  const [creandoUsuario, setCreandoUsuario] = useState(false);

  useEffect(() => {
    cargarConsultas();
  }, [filtroEstado]);

  const cargarConsultas = async () => {
    try {
      setLoading(true);
      const params = filtroEstado !== 'todas' ? `?estado=${filtroEstado}` : '';
      const response = await axios.get(`${API}/admin/consultas-visitantes${params}`);
      setConsultas(response.data.consultas);
      setEstadisticas(response.data.estadisticas);
    } catch (error) {
      console.error('Error cargando consultas:', error);
      toast.error('Error al cargar consultas');
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = async (consulta) => {
    try {
      const response = await axios.get(`${API}/admin/consultas-visitantes/${consulta.id}`);
      setConsultaSeleccionada(response.data);
      // Recargar lista para actualizar estado
      cargarConsultas();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al obtener detalle');
    }
  };

  const abrirDialogoRespuesta = (consulta) => {
    setConsultaSeleccionada(consulta);
    setPlantillaSeleccionada('general');
    setMensajePersonalizado('');
    setMostrarDialogoRespuesta(true);
  };

  const enviarInstrucciones = async () => {
    if (!consultaSeleccionada) return;

    setEnviandoEmail(true);
    try {
      await axios.post(
        `${API}/admin/consultas-visitantes/${consultaSeleccionada.id}/enviar-instrucciones`,
        {
          plantilla: plantillaSeleccionada,
          mensaje_personalizado: mensajePersonalizado
        }
      );
      toast.success('✅ Instrucciones enviadas correctamente');
      setMostrarDialogoRespuesta(false);
      cargarConsultas();
      setConsultaSeleccionada(null);
      setMensajePersonalizado('');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar instrucciones');
    } finally {
      setEnviandoEmail(false);
    }
  };

  const eliminarConsulta = async (consultaId) => {
    if (!window.confirm('¿Eliminar esta consulta permanentemente?')) return;

    try {
      await axios.delete(`${API}/admin/consultas-visitantes/${consultaId}`);
      toast.success('Consulta eliminada');
      cargarConsultas();
      if (consultaSeleccionada?.id === consultaId) {
        setConsultaSeleccionada(null);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar consulta');
    }
  };

  const marcarSpam = async (consultaId) => {
    if (!window.confirm('¿Marcar esta consulta como SPAM?')) return;

    try {
      await axios.put(`${API}/admin/consultas-visitantes/${consultaId}/marcar-spam`);
      toast.success('Marcada como spam');
      cargarConsultas();
      if (consultaSeleccionada?.id === consultaId) {
        setConsultaSeleccionada(null);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al marcar como spam');
    }
  };

  const crearUsuarioDesdeConsulta = async (consulta) => {
    if (!window.confirm(`¿Crear usuario estudiante para ${consulta.nombre_completo}?\n\nPassword: consul01\nSe enviará email con credenciales.`)) {
      return;
    }

    setCreandoUsuario(true);
    try {
      const response = await axios.post(
        `${API}/admin/consultas-visitantes/${consulta.id}/crear-usuario`
      );
      
      toast.success(`✅ Usuario creado exitosamente!\n📧 Email enviado a ${consulta.email}`);
      cargarConsultas();
      setConsultaSeleccionada(null);
    } catch (error) {
      console.error('Error:', error);
      const mensaje = error.response?.data?.detail || 'Error al crear usuario';
      toast.error(mensaje);
    } finally {
      setCreandoUsuario(false);
    }
  };

  // Estado para pasar a marketing
  const [pasandoAMarketing, setPasandoAMarketing] = useState(false);

  // Función para pasar consulta a Marketing
  const pasarAMarketing = async (consulta) => {
    if (!window.confirm(`¿Pasar a "${consulta.nombre_completo}" al Pipeline de Marketing?\n\nSe creará como prospecto en etapa "Interesado".`)) {
      return;
    }

    setPasandoAMarketing(true);
    try {
      const response = await axios.post(
        `${API}/marketing/prospectos`,
        {
          nombre: consulta.nombre || consulta.nombre_completo?.split(' ')[0] || '',
          apellido: consulta.apellido || consulta.nombre_completo?.split(' ').slice(1).join(' ') || '',
          email: consulta.email || '',
          telefono: consulta.telefono || consulta.celular || '',
          etapa: 'interesado',
          fuente: 'web',
          notas: `Consulta original: ${consulta.mensaje || consulta.consulta || ''}\n\nPaís: ${consulta.pais || 'N/A'}\nCiudad: ${consulta.ciudad || 'N/A'}\nFecha consulta: ${consulta.fecha_creacion ? new Date(consulta.fecha_creacion).toLocaleDateString('es-UY') : 'N/A'}`
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.success) {
        toast.success(`✅ ${consulta.nombre_completo} pasado a Marketing como "Interesado"`);
        cargarConsultas();
        setConsultaSeleccionada(null);
      }
    } catch (error) {
      console.error('Error:', error);
      const mensaje = error.response?.data?.detail || 'Error al pasar a Marketing';
      toast.error(mensaje);
    } finally {
      setPasandoAMarketing(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const configs = {
      nueva: { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle, text: 'Nueva' },
      en_revision: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, text: 'En Revisión' },
      atendida: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, text: 'Atendida' },
      spam: { color: 'bg-gray-100 text-gray-800 border-gray-300', icon: XCircle, text: 'Spam' }
    };
    const config = configs[estado] || configs.nueva;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con Estadísticas */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Consultas de Visitantes</h2>
              <p className="text-purple-100">Gestión de solicitudes de información</p>
            </div>
            <Mail className="w-12 h-12 text-white opacity-80" />
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Estadísticas */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-3xl font-bold text-blue-600">{estadisticas.total || 0}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100" onClick={() => setFiltroEstado('nueva')}>
              <p className="text-sm text-gray-600">Nuevas</p>
              <p className="text-3xl font-bold text-red-600">{estadisticas.nuevas || 0}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100" onClick={() => setFiltroEstado('en_revision')}>
              <p className="text-sm text-gray-600">En Revisión</p>
              <p className="text-3xl font-bold text-yellow-600">{estadisticas.en_revision || 0}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100" onClick={() => setFiltroEstado('atendida')}>
              <p className="text-sm text-gray-600">Atendidas</p>
              <p className="text-3xl font-bold text-green-600">{estadisticas.atendidas || 0}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => setFiltroEstado('spam')}>
              <p className="text-sm text-gray-600">Spam</p>
              <p className="text-3xl font-bold text-gray-600">{estadisticas.spam || 0}</p>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filtroEstado === 'todas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('todas')}
              size="sm"
            >
              Todas
            </Button>
            <Button
              variant={filtroEstado === 'nueva' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('nueva')}
              size="sm"
            >
              Nuevas ({estadisticas.nuevas || 0})
            </Button>
            <Button
              variant={filtroEstado === 'en_revision' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('en_revision')}
              size="sm"
            >
              En Revisión
            </Button>
            <Button
              variant={filtroEstado === 'atendida' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('atendida')}
              size="sm"
            >
              Atendidas
            </Button>
            <Button
              variant={filtroEstado === 'spam' ? 'default' : 'outline'}
              onClick={() => setFiltroEstado('spam')}
              size="sm"
            >
              Spam
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Consultas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Columna Izquierda: Lista */}
        <div className="space-y-4">
          {consultas.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <Mail className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No hay consultas para mostrar</p>
              </CardContent>
            </Card>
          ) : (
            consultas.map((consulta) => (
              <Card
                key={consulta.id}
                className={`cursor-pointer transition hover:shadow-lg ${
                  consultaSeleccionada?.id === consulta.id ? 'ring-2 ring-purple-500' : ''
                }`}
                onClick={() => verDetalle(consulta)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {consulta.nombre_completo}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {consulta.email}
                      </p>
                    </div>
                    {getEstadoBadge(consulta.estado)}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-3">
                    <p className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {consulta.ciudad}, {consulta.pais}
                    </p>
                    <p className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatearFecha(consulta.fecha_creacion)}
                    </p>
                  </div>

                  {consulta.centro_interes && (
                    <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                      🏫 {consulta.centro_interes}
                    </p>
                  )}

                  {consulta.admin_revisor_nombre && (
                    <p className="text-xs text-gray-500 mt-2">
                      Revisado por: {consulta.admin_revisor_nombre}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Columna Derecha: Detalle */}
        {consultaSeleccionada && (
          <div className="lg:sticky lg:top-4">
            <Card>
              <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold">{consultaSeleccionada.nombre_completo}</h3>
                    <p className="text-sm text-blue-100">{consultaSeleccionada.email}</p>
                  </div>
                  {getEstadoBadge(consultaSeleccionada.estado)}
                </div>
              </CardHeader>

              <CardContent className="p-6 space-y-4">
                {/* Información de Contacto */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">Teléfono:</span>
                    <span>{consultaSeleccionada.telefono}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">Ubicación:</span>
                    <span>{consultaSeleccionada.ciudad}, {consultaSeleccionada.pais}</span>
                  </div>
                  {consultaSeleccionada.centro_interes && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <span>🏫</span>
                      <span className="font-semibold">Centro de Interés:</span>
                      <span>{consultaSeleccionada.centro_interes}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">Fecha:</span>
                    <span>{formatearFecha(consultaSeleccionada.fecha_creacion)}</span>
                  </div>
                </div>

                {/* Mensaje */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-gray-700">Mensaje:</span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{consultaSeleccionada.mensaje}</p>
                </div>

                {/* Info adicional */}
                {consultaSeleccionada.admin_revisor_nombre && (
                  <div className="bg-blue-50 p-3 rounded border border-blue-200 text-sm">
                    <p className="text-gray-700">
                      <strong>Revisado por:</strong> {consultaSeleccionada.admin_revisor_nombre}
                    </p>
                  </div>
                )}

                {consultaSeleccionada.fecha_atencion && (
                  <div className="bg-green-50 p-3 rounded border border-green-200 text-sm">
                    <p className="text-gray-700">
                      <strong>Atendida el:</strong> {formatearFecha(consultaSeleccionada.fecha_atencion)}
                    </p>
                  </div>
                )}

                {/* Acciones */}
                <div className="space-y-2 pt-4">
                  {/* Botón Pasar a Marketing - Nuevo */}
                  {consultaSeleccionada.estado !== 'spam' && (
                    <Button
                      onClick={() => pasarAMarketing(consultaSeleccionada)}
                      disabled={pasandoAMarketing}
                      className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white shadow-lg"
                    >
                      <span className="flex items-center justify-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        {pasandoAMarketing ? 'Pasando...' : '📊 Pasar a Marketing'}
                      </span>
                    </Button>
                  )}

                  {/* Botón Crear Usuario - Principal y destacado */}
                  {consultaSeleccionada.estado !== 'spam' && consultaSeleccionada.email && (
                    <Button
                      onClick={() => crearUsuarioDesdeConsulta(consultaSeleccionada)}
                      disabled={creandoUsuario}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg"
                    >
                      <span className="flex items-center justify-center">
                        <User className="w-4 h-4 mr-2" />
                        {creandoUsuario ? 'Creando Usuario...' : '👤 Crear Usuario (Estudiante)'}
                      </span>
                    </Button>
                  )}

                  {consultaSeleccionada.estado !== 'spam' && (
                    <Button
                      onClick={() => abrirDialogoRespuesta(consultaSeleccionada)}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                    >
                      <span className="flex items-center justify-center">
                        <Send className="w-4 h-4 mr-2" />
                        {consultaSeleccionada.estado === 'atendida' ? 'Reenviar Email' : 'Responder con Email'}
                      </span>
                    </Button>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => marcarSpam(consultaSeleccionada.id)}
                      variant="outline"
                      className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Marcar Spam
                    </Button>
                    <Button
                      onClick={() => eliminarConsulta(consultaSeleccionada.id)}
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={() => setConsultaSeleccionada(null)}
                  variant="ghost"
                  className="w-full"
                >
                  Cerrar
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Dialog para seleccionar plantilla y responder */}
      <Dialog open={mostrarDialogoRespuesta} onOpenChange={setMostrarDialogoRespuesta}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl">
              <Mail className="mr-2 text-blue-600" size={24} />
              Responder a {consultaSeleccionada?.nombre_completo}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Selector de Plantilla */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                📧 Selecciona la Plantilla de Email
              </label>
              <div className="grid grid-cols-1 gap-3">
                <div 
                  onClick={() => setPlantillaSeleccionada('general')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    plantillaSeleccionada === 'general' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-start">
                    <FileText className={`mr-3 mt-1 ${plantillaSeleccionada === 'general' ? 'text-blue-600' : 'text-gray-400'}`} size={20} />
                    <div>
                      <h4 className="font-semibold text-gray-900">Plantilla 1: Información General</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Incluye: requisitos, modalidad, horarios y contacto
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setPlantillaSeleccionada('fechas')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    plantillaSeleccionada === 'fechas' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className="flex items-start">
                    <Calendar className={`mr-3 mt-1 ${plantillaSeleccionada === 'fechas' ? 'text-orange-600' : 'text-gray-400'}`} size={20} />
                    <div>
                      <h4 className="font-semibold text-gray-900">Plantilla 2: Próximo Inicio de Clases</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Incluye: fechas de inicio, cierre de inscripción y periodo académico
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setPlantillaSeleccionada('costos')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    plantillaSeleccionada === 'costos' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-start">
                    <i className={`fas fa-dollar-sign mr-3 mt-2 ${plantillaSeleccionada === 'costos' ? 'text-green-600' : 'text-gray-400'}`}></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Plantilla 3: Información con Costos</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Incluye: matrícula, costos semestrales, becas y formas de pago
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setPlantillaSeleccionada('completa')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    plantillaSeleccionada === 'completa' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-start">
                    <i className={`fas fa-star mr-3 mt-2 ${plantillaSeleccionada === 'completa' ? 'text-purple-600' : 'text-gray-400'}`}></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Plantilla 4: Información Completa ⭐</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Incluye TODO: fechas + costos + requisitos + modalidad + horarios
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mensaje Personalizado (Opcional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💬 Mensaje Personalizado (Opcional)
              </label>
              <Textarea
                value={mensajePersonalizado}
                onChange={(e) => setMensajePersonalizado(e.target.value)}
                placeholder="Agrega un mensaje personalizado que se incluirá al final del email (opcional)..."
                rows={4}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este mensaje aparecerá como una nota especial antes del cierre del email
              </p>
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> La información de fechas, costos y requisitos se toma de la 
                <strong> Configuración de Emails</strong> en el menú de Comunicaciones
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setMostrarDialogoRespuesta(false)}
              disabled={enviandoEmail}
            >
              Cancelar
            </Button>
            <Button
              onClick={enviarInstrucciones}
              disabled={enviandoEmail}
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
            >
              {enviandoEmail ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionConsultasVisitantes;
